import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Joi from 'joi';
import {
  Switch,
  Button,
  Typography,
  TypographyType,
  TypographyVariant,
  Icon,
} from 'src/components-dummy';
import { AvailableIcons } from 'src/components-dummy/Icon/AvailableIcons';
import {
  TourAndGallerySettings as TourAndGallerySettingsData,
  TourAndGalleryCuratedImage,
  IInspirationGalleryCatalogPreviewProduct,
} from 'src/services/src/service/tour-and-gallery/types';
import { MetaTypes } from 'src/services';
import { Dispatch } from 'src/components-bl/types';
import { tourAndGalleryService } from 'src/services/src/service/tour-and-gallery/tour-and-gallery.service';
import { tourAndGalleryActionsMethods } from './Actions/TourAndGalleryActions';
import {
  SettingsLayout,
  SettingsPanel,
  PreviewPanel,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SourceButtonsRow,
  SourceButton,
  SourceButtonInner,
  SourceButtonIcon,
  SourceButtonTitle,
  SourceButtonSubtitle,
  PersonalizeBox,
  PersonalizeBoxIcon,
  ToggleRow,
  AdvancedHeader,
  AdvancedChevron,
  PreviewHeader,
  PreviewTitle,
  PreviewCount,
  PreviewGrid,
  PreviewItem,
  PreviewItemEmpty,
  PreviewItemEmptyLabel,
  PreviewProductImage,
  EmptyPreviewState,
  EmptyPreviewIcon,
  EmptyPreviewText,
  FormActions,
  CurateTableHeader,
  CurateColumnLabel,
  CurateRowsWrapper,
  CurateRow,
  ImageSwatch,
  CurateFieldWrapper,
  CurateInput,
  CurateFieldError,
  CurateFieldWarning,
  DragHandleIcon,
  DeleteRowButton,
  AddUrlLink,
  AddLanguageButton,
  BulkAddText,
  BrokenImagePlaceholder,
  LocaleUrlsToggle,
  LocaleUrlsSection,
  LocaleUrlRow as LocaleUrlRowStyled,
  LocaleRowsWrapper,
  LocaleSelectInput,
  LocaleUrlHint,
  LocaleUrlInput,
} from './TourAndGallerySettings.styles';

enum GalleryImageSource {
  FromCatalog = 'fromCatalog',
  CurateGallery = 'curateGallery',
}

interface LocaleUrlRow {
  id: string;
  locale: string;
  url: string;
  urlError?: string;
}

interface CurateImageRow {
  id: string;
  imageUrl: string;
  sku: string;
  altText: string;
  skuError?: string;
  imageUrlError?: string;
  imageLoadError?: boolean;
  isSkuValidating?: boolean;
}

const urlSchema = Joi.string().uri();

const validateUrl = (url: string): string | undefined => {
  if (!url) return undefined;
  return urlSchema.validate(url).error ? 'Please enter a valid URL' : undefined;
};

export interface TourAndGallerySettingsProps {
  settings: TourAndGallerySettingsData | null;
  shopId: number;
  isLoading: boolean;
  dispatch: Dispatch;
  languages?: MetaTypes.Language[];
}

const SOURCE_OPTIONS = [
  {
    value: GalleryImageSource.FromCatalog,
    title: 'From your catalog',
    subtitle: 'Default',
    icon: AvailableIcons.CatalogManager,
  },
  {
    value: GalleryImageSource.CurateGallery,
    title: 'Curate gallery',
    subtitle: 'Bulk URLs',
    icon: AvailableIcons.Upload,
  },
];

const CURATE_PREVIEW_GRID_SIZE = 18;

function skusToRows(skus: TourAndGalleryCuratedImage[], images: string[]): CurateImageRow[] {
  const fromSkus: CurateImageRow[] = skus.map((s, i) => ({
    id: `sku-${i}`,
    imageUrl: s.imageUrl,
    sku: s.sku ?? '',
    altText: s.altText ?? '',
  }));
  const skuUrls = new Set(skus.map(s => s.imageUrl));
  const fromImages: CurateImageRow[] = images
    .filter(url => !skuUrls.has(url))
    .map((url, i) => ({
      id: `img-${i}`,
      imageUrl: url,
      sku: '',
      altText: '',
    }));
  return [...fromSkus, ...fromImages];
}

function settingsToLocalState(settings: TourAndGallerySettingsData) {
  const urlsByLocale = settings.advancedSettings.infoPage?.urlsByLocale ?? {};
  const localeUrlRows: LocaleUrlRow[] = Object.entries(urlsByLocale).map(([locale, url], i) => ({
    id: `loc-${i}`,
    locale,
    url,
  }));

  return {
    gallerySource:
      settings.curatedGallery.images.length > 0
        ? GalleryImageSource.CurateGallery
        : GalleryImageSource.FromCatalog,
    personalizeToShopper: settings.catalogGallery.personalization.enabled,
    curateRows: skusToRows(settings.curatedGallery.images, []),
    displayTour: settings.advancedSettings.displayVisualSearchTour,
    infoPageUrlEnabled: !!(
      settings.advancedSettings.infoPage?.url || settings.advancedSettings.infoPage?.urlsByLocale
    ),
    infoPageUrl: settings.advancedSettings.infoPage?.url ?? '',
    localeUrlRows,
    isLocaleUrlsOpen: localeUrlRows.length > 0,
    showFilters: settings.advancedSettings.resultsModal.showFilters,
    showSortMenu: settings.advancedSettings.resultsModal.showSortMenu,
  };
}

function localStateToSettings(
  state: ReturnType<typeof settingsToLocalState>,
  original: TourAndGallerySettingsData,
  catalogProducts: IInspirationGalleryCatalogPreviewProduct[]
): TourAndGallerySettingsData {
  const curatedImages: TourAndGalleryCuratedImage[] = state.curateRows
    .filter(r => r.imageUrl && !r.imageUrlError)
    .map(r => ({
      imageUrl: r.imageUrl,
      sku: r.sku || undefined,
      altText: r.altText || undefined,
    }));

  const catalogImages =
    state.gallerySource === GalleryImageSource.FromCatalog
      ? catalogProducts.map(p => p.imageUrl as string).filter(Boolean)
      : [];

  return {
    catalogGallery: {
      personalization: { enabled: state.personalizeToShopper },
      images: catalogImages,
    },
    curatedGallery: {
      images: state.gallerySource === GalleryImageSource.CurateGallery ? curatedImages : [],
    },
    advancedSettings: {
      displayVisualSearchTour: state.displayTour,
      infoPage: {
        url: state.infoPageUrlEnabled ? state.infoPageUrl || undefined : undefined,
        urlsByLocale:
          state.infoPageUrlEnabled && state.localeUrlRows.length
            ? Object.fromEntries(
                state.localeUrlRows.filter(r => r.locale).map(r => [r.locale, r.url])
              )
            : undefined,
      },
      resultsModal: {
        showFilters: state.showFilters,
        showSortMenu: state.showSortMenu,
      },
    },
  };
}

function normalizeCurateRows(rows: CurateImageRow[]) {
  return rows.map(({ imageUrl, sku, altText }) => ({ imageUrl, sku, altText }));
}

function normalizeLocaleUrlRows(rows: LocaleUrlRow[]) {
  return rows.map(({ locale, url }) => ({ locale, url }));
}

interface CurateRowItemProps {
  row: CurateImageRow;
  index: number;
  isDragging: boolean;
  isOver: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
  onUpdate: (id: string, field: keyof CurateImageRow, value: string) => void;
  onDelete: (id: string) => void;
  onBulkPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onImageError: (id: string) => void;
  onImageErrorClear: (id: string) => void;
}

const CurateRowItem = React.memo(
  ({
    row,
    index,
    isDragging,
    isOver,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onUpdate,
    onDelete,
    onBulkPaste,
    onImageError,
    onImageErrorClear,
  }: CurateRowItemProps) => (
    <CurateRow
      onDragOver={e => onDragOver(e, index)}
      onDrop={() => onDrop(index)}
      onDragEnd={onDragEnd}
      style={{
        opacity: isDragging ? 0.4 : 1,
        borderTop: isOver ? '2px solid #0073ff' : '2px solid transparent',
        transition: 'opacity 0.15s',
      }}
    >
      <DragHandleIcon draggable onDragStart={() => onDragStart(index)}>
        <Icon name={AvailableIcons.DragHandleVerticalDouble} />
      </DragHandleIcon>
      {row.imageUrl && !row.imageLoadError ? (
        <ImageSwatch
          src={row.imageUrl}
          alt={row.altText || ''}
          onError={() => onImageError(row.id)}
        />
      ) : (
        <BrokenImagePlaceholder hasError={!!row.imageLoadError && !!row.imageUrl} />
      )}
      <CurateFieldWrapper>
        <CurateInput
          value={row.imageUrl}
          placeholder='https://cdn.example.com/img.jpg'
          onChange={e => {
            onUpdate(row.id, 'imageUrl', e.target.value);
            if (row.imageLoadError) onImageErrorClear(row.id);
          }}
          onPaste={onBulkPaste}
          error={!!row.imageUrlError}
        />
        {row.imageUrlError && <CurateFieldError>{row.imageUrlError}</CurateFieldError>}
      </CurateFieldWrapper>
      <CurateFieldWrapper>
        <CurateInput
          value={row.sku}
          placeholder={row.isSkuValidating ? 'Checking…' : 'Optional'}
          onChange={e => onUpdate(row.id, 'sku', e.target.value)}
        />
        {row.skuError && <CurateFieldWarning>{row.skuError}</CurateFieldWarning>}
      </CurateFieldWrapper>
      <CurateFieldWrapper>
        <CurateInput
          value={row.altText}
          placeholder='Recommended'
          onChange={e => onUpdate(row.id, 'altText', e.target.value)}
        />
      </CurateFieldWrapper>
      <DeleteRowButton onClick={() => onDelete(row.id)} aria-label='Remove row'>
        <Icon name={AvailableIcons.TrashCan} />
      </DeleteRowButton>
    </CurateRow>
  )
);

export const TourAndGallerySettings = ({
  settings,
  shopId,
  isLoading,
  dispatch,
  languages,
}: TourAndGallerySettingsProps): JSX.Element => {
  const [gallerySource, setGallerySource] = useState(GalleryImageSource.FromCatalog);
  const gallerySourceRef = useRef(gallerySource);
  const [personalizeToShopper, setPersonalizeToShopper] = useState(false);
  const [curateRows, setCurateRows] = useState<CurateImageRow[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<
    IInspirationGalleryCatalogPreviewProduct[]
  >([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isCatalogError, setIsCatalogError] = useState(false);
  const [displayTour, setDisplayTour] = useState(true);
  const [infoPageUrlEnabled, setInfoPageUrlEnabled] = useState(false);
  const [infoPageUrl, setInfoPageUrl] = useState('');
  const [localeUrlRows, setLocaleUrlRows] = useState<LocaleUrlRow[]>([]);
  const [isLocaleUrlsOpen, setIsLocaleUrlsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showSortMenu, setShowSortMenu] = useState(true);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(true);
  const [infoPageUrlError, setInfoPageUrlError] = useState<string | undefined>();
  const [saveAttempted, setSaveAttempted] = useState(false);
  const dragIndexRef = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const skuDebounceRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const skuAbortRefs = useRef<Map<string, AbortController>>(new Map());

  const setDragIndexBoth = (v: number | null) => {
    dragIndexRef.current = v;
    setDragIndex(v);
  };

  const setGallerySourceBoth = (source: GalleryImageSource) => {
    gallerySourceRef.current = source;
    setGallerySource(source);
  };

  const resetToSettings = useCallback((s: TourAndGallerySettingsData) => {
    const local = settingsToLocalState(s);
    setGallerySourceBoth(local.gallerySource);
    setPersonalizeToShopper(local.personalizeToShopper);
    setCurateRows(local.curateRows);
    setDisplayTour(local.displayTour);
    setInfoPageUrlEnabled(local.infoPageUrlEnabled);
    setInfoPageUrl(local.infoPageUrl);
    setLocaleUrlRows(local.localeUrlRows);
    setIsLocaleUrlsOpen(local.isLocaleUrlsOpen);
    setShowFilters(local.showFilters);
    setShowSortMenu(local.showSortMenu);
    setInfoPageUrlError(undefined);
    if (s.catalogGallery.images.length > 0) {
      setCatalogProducts(s.catalogGallery.images.map(imageUrl => ({ imageUrl })));
    }
    setIsCatalogError(false);
    setSaveAttempted(false);
  }, []);

  useEffect(() => {
    if (!settings) return;
    resetToSettings(settings);
  }, [settings]);

  useEffect(() => {
    if (gallerySourceRef.current !== GalleryImageSource.FromCatalog) return;
    if (!settings) return;
    if (settings.catalogGallery.images.length || catalogProducts.length > 0) return;
    if (isCatalogLoading) return;

    let cancelled = false;
    setIsCatalogLoading(true);
    setIsCatalogError(false);

    (async () => {
      try {
        const products = await tourAndGalleryService.getCatalogPreview(shopId);
        if (!cancelled) {
          setCatalogProducts(products);
        }
      } catch (e) {
        if (!cancelled) {
          setIsCatalogError(true);
          console.error('TourAndGallerySettings: failed to load catalog preview', e);
          dispatch(tourAndGalleryActionsMethods.getCatalogPreviewError({ error: e }));
        }
      } finally {
        if (!cancelled) setIsCatalogLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setIsCatalogLoading(false);
    };
  }, [gallerySource, settings, catalogProducts.length, shopId]);

  const originalLocalState = useMemo(
    () => (settings ? settingsToLocalState(settings) : null),
    [settings]
  );

  const isDirty = useMemo(() => {
    if (!originalLocalState) return false;
    return (
      gallerySource !== originalLocalState.gallerySource ||
      personalizeToShopper !== originalLocalState.personalizeToShopper ||
      displayTour !== originalLocalState.displayTour ||
      infoPageUrlEnabled !== originalLocalState.infoPageUrlEnabled ||
      infoPageUrl !== originalLocalState.infoPageUrl ||
      showFilters !== originalLocalState.showFilters ||
      showSortMenu !== originalLocalState.showSortMenu ||
      JSON.stringify(normalizeCurateRows(curateRows)) !==
        JSON.stringify(normalizeCurateRows(originalLocalState.curateRows)) ||
      JSON.stringify(normalizeLocaleUrlRows(localeUrlRows)) !==
        JSON.stringify(normalizeLocaleUrlRows(originalLocalState.localeUrlRows))
    );
  }, [
    originalLocalState,
    gallerySource,
    personalizeToShopper,
    displayTour,
    infoPageUrlEnabled,
    infoPageUrl,
    showFilters,
    showSortMenu,
    curateRows,
    localeUrlRows,
  ]);

  useEffect(() => {
    dispatch(tourAndGalleryActionsMethods.notifyIsDirty(isDirty));
  }, [isDirty]);

  useEffect(() => {
    return () => {
      dispatch(tourAndGalleryActionsMethods.notifyIsDirty(false));
    };
  }, []);

  useEffect(() => {
    const debounces = skuDebounceRefs.current;
    const aborts = skuAbortRefs.current;
    return () => {
      debounces.forEach(clearTimeout);
      aborts.forEach(c => c.abort());
    };
  }, []);

  const updateCurateRow = useCallback(
    (id: string, field: keyof CurateImageRow, value: string) => {
      if (field === 'sku') {
        clearTimeout(skuDebounceRefs.current.get(id));
        skuAbortRefs.current.get(id)?.abort();

        if (value) {
          const timeoutId = setTimeout(async () => {
            const controller = new AbortController();
            skuAbortRefs.current.set(id, controller);
            try {
              const { exists, ctlImageUrl } = await tourAndGalleryService.checkSku(
                shopId,
                value,
                controller.signal
              );
              setCurateRows(rows =>
                rows.map(row => {
                  if (row.id !== id) return row;
                  const nextImageUrl = ctlImageUrl || row.imageUrl;
                  return {
                    ...row,
                    isSkuValidating: false,
                    skuError: exists ? undefined : 'SKU not found in catalog',
                    imageUrl: nextImageUrl,
                    imageUrlError: validateUrl(nextImageUrl),
                    imageLoadError: ctlImageUrl ? false : row.imageLoadError,
                  };
                })
              );
            } catch {
              setCurateRows(rows =>
                rows.map(r => (r.id === id ? { ...r, isSkuValidating: false } : r))
              );
            }
          }, 500);
          skuDebounceRefs.current.set(id, timeoutId);
        }
      }

      setCurateRows(rows =>
        rows.map(row => {
          if (row.id !== id) return row;
          const updated = { ...row, [field]: value };
          if (field === 'imageUrl') {
            updated.imageUrlError = validateUrl(value);
          }
          if (field === 'sku') {
            updated.skuError = undefined;
            updated.isSkuValidating = !!value;
          }
          return updated;
        })
      );
    },
    [shopId]
  );

  const addCurateRow = useCallback(() => {
    const newId = String(Date.now());
    setCurateRows(rows => [...rows, { id: newId, imageUrl: '', sku: '', altText: '' }]);
  }, []);

  const handleBulkPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const urls = text
      .split(/[\n\r\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (urls.length <= 1) return;
    e.preventDefault();
    const newRows: CurateImageRow[] = urls.map((url, i) => ({
      id: String(Date.now() + i),
      imageUrl: url,
      sku: '',
      altText: '',
      imageUrlError: validateUrl(url),
    }));
    setCurateRows(rows => [...rows.filter(r => r.imageUrl), ...newRows]);
  }, []);

  const deleteCurateRow = useCallback((id: string) => {
    setCurateRows(rows => rows.filter(row => row.id !== id));
  }, []);

  const handleImageError = useCallback((id: string) => {
    setCurateRows(rows => rows.map(r => (r.id === id ? { ...r, imageLoadError: true } : r)));
  }, []);

  const handleImageErrorClear = useCallback((id: string) => {
    setCurateRows(rows => rows.map(r => (r.id === id ? { ...r, imageLoadError: false } : r)));
  }, []);

  const handleDragStart = useCallback((index: number) => setDragIndexBoth(index), []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((toIndex: number) => {
    const from = dragIndexRef.current;
    if (from !== null && from !== toIndex) {
      setCurateRows(rows => {
        const next = [...rows];
        const [moved] = next.splice(from, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    }
    setDragIndexBoth(null);
    setDragOverIndex(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragIndexBoth(null);
    setDragOverIndex(null);
  }, []);

  const addLocaleUrl = useCallback(() => {
    setLocaleUrlRows(rows => [...rows, { id: String(Date.now()), locale: '', url: '' }]);
  }, []);

  const updateLocaleUrlRow = useCallback((id: string, field: 'locale' | 'url', value: string) => {
    setLocaleUrlRows(rows =>
      rows.map(r => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        if (field === 'url') updated.urlError = validateUrl(value);
        return updated;
      })
    );
  }, []);

  const deleteLocaleUrl = useCallback((id: string) => {
    setLocaleUrlRows(rows => rows.filter(r => r.id !== id));
  }, []);

  const isCurateMode = gallerySource === GalleryImageSource.CurateGallery;

  const validCuratedRowsCount = curateRows.filter(row => row.imageUrl && !row.imageUrlError).length;

  const hasNoCuratedImages = isCurateMode && validCuratedRowsCount === 0;

  const hasTooManyCuratedImages = isCurateMode && validCuratedRowsCount > CURATE_PREVIEW_GRID_SIZE;

  const handleSave = () => {
    if (hasNoCuratedImages || hasTooManyCuratedImages) {
      setSaveAttempted(true);
      return;
    }

    const base: TourAndGallerySettingsData = settings ?? {
      catalogGallery: { personalization: { enabled: false }, images: [] },
      curatedGallery: { images: [] },
      advancedSettings: {
        displayVisualSearchTour: true,
        resultsModal: { showFilters: true, showSortMenu: true },
      },
    };
    const updated = localStateToSettings(
      {
        gallerySource,
        personalizeToShopper,
        curateRows,
        displayTour,
        infoPageUrlEnabled,
        infoPageUrl,
        localeUrlRows,
        isLocaleUrlsOpen,
        showFilters,
        showSortMenu,
      },
      base,
      catalogProducts ?? []
    );
    dispatch(tourAndGalleryActionsMethods.updateSettings({ shopId, settings: updated }));
  };

  const handleCancel = () => {
    if (settings) resetToSettings(settings);
  };

  const hasValidationErrors =
    (infoPageUrlEnabled && !!infoPageUrlError) ||
    (infoPageUrlEnabled && localeUrlRows.some(r => !!r.urlError)) ||
    (isCurateMode && curateRows.some(row => !!row.imageUrlError));
  const isSaveBlockedByCatalogError = !!isCatalogError && !isCurateMode;
  const hasNoCatalogImages =
    !isCurateMode &&
    !isCatalogLoading &&
    !isCatalogError &&
    !catalogProducts.some(p => !!p.imageUrl);
  const isPersonalizedPreview =
    gallerySource === GalleryImageSource.FromCatalog && personalizeToShopper;

  const curatePreviewItems = curateRows
    .slice(0, CURATE_PREVIEW_GRID_SIZE)
    .map(row => ({ imageUrl: row.imageUrl }));

  const renderPreview = () => {
    if (isPersonalizedPreview) {
      return (
        <EmptyPreviewState>
          <EmptyPreviewIcon>
            <Icon name={AvailableIcons.User} />
          </EmptyPreviewIcon>
          <EmptyPreviewText variant={TypographyVariant.SmallRegular} type={TypographyType.Body}>
            Personalized preview unavailable
          </EmptyPreviewText>
          <EmptyPreviewText variant={TypographyVariant.SmallRegular} type={TypographyType.Body}>
            Sample-user follow-up needed
          </EmptyPreviewText>
        </EmptyPreviewState>
      );
    }

    if (isCurateMode) {
      return (
        <PreviewGrid>
          {curatePreviewItems.map((item, i) =>
            item?.imageUrl ? (
              <PreviewItem key={i}>
                <PreviewProductImage src={item.imageUrl} alt='' />
              </PreviewItem>
            ) : (
              <PreviewItemEmpty key={i}>
                <PreviewItemEmptyLabel>empty</PreviewItemEmptyLabel>
              </PreviewItemEmpty>
            )
          )}
        </PreviewGrid>
      );
    }

    const catalogItemsToRender: (IInspirationGalleryCatalogPreviewProduct | null)[] =
      isCatalogLoading
        ? Array(CURATE_PREVIEW_GRID_SIZE).fill(null)
        : catalogProducts?.length
          ? catalogProducts.slice(0, CURATE_PREVIEW_GRID_SIZE)
          : [];

    if (catalogItemsToRender.length > 0) {
      return (
        <PreviewGrid>
          {catalogItemsToRender.map((product, i) =>
            product ? (
              <PreviewItem key={i}>
                <PreviewProductImage
                  src={product.imageUrl as string}
                  alt={(product.title as string) ?? ''}
                />
              </PreviewItem>
            ) : (
              <PreviewItemEmpty key={i} />
            )
          )}
        </PreviewGrid>
      );
    }

    return (
      <EmptyPreviewState>
        <EmptyPreviewText variant={TypographyVariant.SmallRegular} type={TypographyType.Body}>
          {isCatalogError ? 'Could not load catalog products' : 'No products to preview'}
        </EmptyPreviewText>
      </EmptyPreviewState>
    );
  };

  const previewCount = () => {
    if (isPersonalizedPreview) return 'Personalized';
    if (isCurateMode)
      return `Showing ${curateRows.filter(r => r.imageUrl).length} of ${CURATE_PREVIEW_GRID_SIZE}`;
    if (isCatalogLoading) return 'Loading…';
    const total = catalogProducts?.length ?? 0;
    const count = Math.min(total, CURATE_PREVIEW_GRID_SIZE);
    return count > 0 ? `Showing ${count} of ${total} products` : '';
  };

  const formButtons = (
    <>
      <Button onClick={handleCancel} variant='tertiary' disabled={!isDirty || isLoading}>
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        variant='primary'
        disabled={
          !isDirty ||
          isLoading ||
          hasValidationErrors ||
          isSaveBlockedByCatalogError ||
          hasNoCatalogImages ||
          hasTooManyCuratedImages
        }
      >
        Save
      </Button>
    </>
  );

  return (
    <>
      <SettingsLayout>
        <SettingsPanel>
          <SectionCard>
            <SectionHeader>
              <SectionTitle variant={TypographyVariant.MediumBold} type={TypographyType.Body}>
                Gallery image source
              </SectionTitle>
              <SectionDescription
                variant={TypographyVariant.SmallRegular}
                type={TypographyType.Body}
              >
                Choose where the inspiration gallery pulls images from
              </SectionDescription>
            </SectionHeader>
            <SourceButtonsRow>
              {SOURCE_OPTIONS.map(option => (
                <SourceButton
                  key={option.value}
                  selected={gallerySource === option.value}
                  onClick={() => {
                    setGallerySourceBoth(option.value);
                  }}
                >
                  <SourceButtonInner>
                    <SourceButtonIcon selected={gallerySource === option.value}>
                      <Icon name={option.icon} />
                    </SourceButtonIcon>
                    <div>
                      <SourceButtonTitle selected={gallerySource === option.value}>
                        {option.title}
                      </SourceButtonTitle>
                      <SourceButtonSubtitle selected={gallerySource === option.value}>
                        {option.subtitle}
                      </SourceButtonSubtitle>
                    </div>
                  </SourceButtonInner>
                </SourceButton>
              ))}
            </SourceButtonsRow>
          </SectionCard>

          {gallerySource === GalleryImageSource.FromCatalog && (
            <SectionCard>
              <SectionHeader>
                <SectionTitle variant={TypographyVariant.MediumBold} type={TypographyType.Body}>
                  Trending products from your catalog
                </SectionTitle>
                <SectionDescription
                  variant={TypographyVariant.SmallRegular}
                  type={TypographyType.Body}
                >
                  {`Top ${CURATE_PREVIEW_GRID_SIZE} products by views, refreshed daily`}
                </SectionDescription>
              </SectionHeader>
              <PersonalizeBox>
                <PersonalizeBoxIcon>
                  <Icon name={AvailableIcons.Stars} />
                </PersonalizeBoxIcon>
                <Switch
                  checked={personalizeToShopper}
                  onChange={checked => {
                    setPersonalizeToShopper(checked);
                  }}
                >
                  <Switch.TitleTemplate>
                    <Typography
                      variant={TypographyVariant.MediumRegular}
                      type={TypographyType.Body}
                    >
                      Personalize to the shopper
                    </Typography>
                  </Switch.TitleTemplate>
                  <Switch.SubTitleTemplate>
                    <Typography variant={TypographyVariant.SmallRegular} type={TypographyType.Body}>
                      Each shopper sees recs tailored to them
                    </Typography>
                  </Switch.SubTitleTemplate>
                </Switch>
              </PersonalizeBox>
            </SectionCard>
          )}

          {isCurateMode && (
            <SectionCard>
              <SectionHeader>
                <SectionTitle variant={TypographyVariant.MediumBold} type={TypographyType.Body}>
                  Curate image URLs
                </SectionTitle>
                <SectionDescription
                  variant={TypographyVariant.SmallRegular}
                  type={TypographyType.Body}
                >
                  Add image URLs, optionally tag with SKU and alt-text
                </SectionDescription>
              </SectionHeader>

              <CurateTableHeader>
                <span />
                <span />
                <CurateColumnLabel>Image URL</CurateColumnLabel>
                <CurateColumnLabel>SKU</CurateColumnLabel>
                <CurateColumnLabel>Alt-text</CurateColumnLabel>
                <span />
              </CurateTableHeader>

              <CurateRowsWrapper>
                {curateRows.map((row, index) => (
                  <CurateRowItem
                    key={row.id}
                    row={row}
                    index={index}
                    isDragging={dragIndex === index}
                    isOver={dragOverIndex === index && dragIndex !== index}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onUpdate={updateCurateRow}
                    onDelete={deleteCurateRow}
                    onBulkPaste={handleBulkPaste}
                    onImageError={handleImageError}
                    onImageErrorClear={handleImageErrorClear}
                  />
                ))}
              </CurateRowsWrapper>

              {saveAttempted && hasNoCuratedImages && (
                <CurateFieldError>
                  At least one image must be added to curate the gallery
                </CurateFieldError>
              )}

              {hasTooManyCuratedImages && (
                <CurateFieldError>
                  {`Curate gallery can contain up to ${CURATE_PREVIEW_GRID_SIZE} images. Remove ${
                    validCuratedRowsCount - CURATE_PREVIEW_GRID_SIZE
                  } to save.`}
                </CurateFieldError>
              )}

              <AddUrlLink
                onClick={addCurateRow}
                disabled={curateRows.length >= CURATE_PREVIEW_GRID_SIZE}
              >
                Add Image URL
              </AddUrlLink>
              <BulkAddText>Bulk add: paste multiple URLs at once</BulkAddText>
            </SectionCard>
          )}

          <SectionCard style={{ padding: '14px 16px' }}>
            <SectionHeader
              style={{
                borderBottom: 'none',
                marginBottom: 0,
                paddingBottom: isAdvancedOpen ? 10 : 0,
              }}
            >
              <AdvancedHeader onClick={() => setIsAdvancedOpen(v => !v)}>
                <SectionTitle variant={TypographyVariant.MediumBold} type={TypographyType.Body}>
                  Advanced Settings
                </SectionTitle>
                <AdvancedChevron open={isAdvancedOpen}>
                  <Icon name={AvailableIcons.ChevronLeft} />
                </AdvancedChevron>
              </AdvancedHeader>
            </SectionHeader>

            {isAdvancedOpen && (
              <>
                <ToggleRow>
                  <Switch
                    checked={displayTour}
                    onChange={checked => {
                      setDisplayTour(checked);
                    }}
                  >
                    <Switch.TitleTemplate>
                      <Typography
                        variant={TypographyVariant.MediumRegular}
                        type={TypographyType.Body}
                      >
                        Display visual search tour
                      </Typography>
                    </Switch.TitleTemplate>
                    <Switch.SubTitleTemplate>
                      <Typography
                        variant={TypographyVariant.SmallRegular}
                        type={TypographyType.Body}
                      >
                        Optional first-time overlay
                      </Typography>
                    </Switch.SubTitleTemplate>
                  </Switch>
                </ToggleRow>

                <ToggleRow>
                  <Switch
                    checked={infoPageUrlEnabled}
                    onChange={checked => {
                      setInfoPageUrlEnabled(checked);
                      setInfoPageUrlError(checked ? validateUrl(infoPageUrl) : undefined);
                    }}
                  >
                    <Switch.TitleTemplate>
                      <Typography
                        variant={TypographyVariant.MediumRegular}
                        type={TypographyType.Body}
                      >
                        Info Page URL
                      </Typography>
                    </Switch.TitleTemplate>
                    <Switch.SubTitleTemplate>
                      <Typography
                        variant={TypographyVariant.SmallRegular}
                        type={TypographyType.Body}
                      >
                        Linked from &quot;Learn more&quot;
                      </Typography>
                    </Switch.SubTitleTemplate>
                  </Switch>
                </ToggleRow>

                {infoPageUrlEnabled && (
                  <>
                    <CurateFieldWrapper>
                      <CurateInput
                        id='info-page-url'
                        value={infoPageUrl}
                        onChange={e => {
                          setInfoPageUrl(e.target.value);
                          setInfoPageUrlError(validateUrl(e.target.value));
                        }}
                        placeholder='https://yoursite.com/about-visual-search'
                        error={!!infoPageUrlError}
                      />
                      {infoPageUrlError && <CurateFieldError>{infoPageUrlError}</CurateFieldError>}
                    </CurateFieldWrapper>

                    <LocaleUrlsToggle onClick={() => setIsLocaleUrlsOpen(v => !v)}>
                      <AdvancedChevron open={isLocaleUrlsOpen}>
                        <Icon name={AvailableIcons.ChevronLeft} />
                      </AdvancedChevron>
                      <Typography
                        variant={TypographyVariant.SmallRegular}
                        type={TypographyType.Body}
                      >
                        Language-specific URLs
                      </Typography>
                      <LocaleUrlHint>— optional</LocaleUrlHint>
                    </LocaleUrlsToggle>

                    {isLocaleUrlsOpen && (
                      <LocaleUrlsSection>
                        {localeUrlRows.length > 0 && (
                          <LocaleRowsWrapper>
                            {localeUrlRows.map(row => (
                              <LocaleUrlRowStyled key={row.id}>
                                <LocaleSelectInput
                                  name={`locale-select-${row.id}`}
                                  value={row.locale}
                                  onChange={e =>
                                    updateLocaleUrlRow(row.id, 'locale', e.target.value)
                                  }
                                >
                                  <option value='' disabled>
                                    Select language
                                  </option>
                                  {languages?.map(lang => (
                                    <option key={lang.locale} value={lang.locale}>
                                      {lang.displayName}
                                    </option>
                                  ))}
                                </LocaleSelectInput>
                                <CurateFieldWrapper>
                                  <LocaleUrlInput
                                    name={`locale-url-${row.id}`}
                                    value={row.url}
                                    placeholder='https://example.com/info'
                                    error={!!row.urlError}
                                    onChange={e =>
                                      updateLocaleUrlRow(row.id, 'url', e.target.value)
                                    }
                                  />
                                  {row.urlError && (
                                    <CurateFieldError>{row.urlError}</CurateFieldError>
                                  )}
                                </CurateFieldWrapper>
                                <DeleteRowButton
                                  onClick={() => deleteLocaleUrl(row.id)}
                                  aria-label='Remove'
                                >
                                  <Icon name={AvailableIcons.TrashCan} />
                                </DeleteRowButton>
                              </LocaleUrlRowStyled>
                            ))}
                          </LocaleRowsWrapper>
                        )}
                        <AddLanguageButton onClick={addLocaleUrl}>+ Add language</AddLanguageButton>
                        <LocaleUrlHint>
                          The default URL above is used whenever a shopper&apos;s language has no
                          specific entry.
                        </LocaleUrlHint>
                      </LocaleUrlsSection>
                    )}
                  </>
                )}

                <ToggleRow>
                  <Switch
                    checked={showFilters}
                    onChange={checked => {
                      setShowFilters(checked);
                    }}
                  >
                    <Switch.TitleTemplate>
                      <Typography
                        variant={TypographyVariant.MediumRegular}
                        type={TypographyType.Body}
                      >
                        Show filters in results modal
                      </Typography>
                    </Switch.TitleTemplate>
                    <Switch.SubTitleTemplate>
                      <Typography
                        variant={TypographyVariant.SmallRegular}
                        type={TypographyType.Body}
                      >
                        Mobile + desktop
                      </Typography>
                    </Switch.SubTitleTemplate>
                  </Switch>
                </ToggleRow>

                <ToggleRow>
                  <Switch
                    checked={showSortMenu}
                    onChange={checked => {
                      setShowSortMenu(checked);
                    }}
                  >
                    <Switch.TitleTemplate>
                      <Typography
                        variant={TypographyVariant.MediumRegular}
                        type={TypographyType.Body}
                      >
                        Show sort menu in results modal
                      </Typography>
                    </Switch.TitleTemplate>
                    <Switch.SubTitleTemplate>
                      <Typography
                        variant={TypographyVariant.SmallRegular}
                        type={TypographyType.Body}
                      >
                        Mobile + desktop
                      </Typography>
                    </Switch.SubTitleTemplate>
                  </Switch>
                </ToggleRow>
              </>
            )}
          </SectionCard>
        </SettingsPanel>

        <PreviewPanel>
          <SectionCard>
            <PreviewHeader>
              <PreviewTitle variant={TypographyVariant.MediumBold} type={TypographyType.Body}>
                Preview
              </PreviewTitle>
              <PreviewCount variant={TypographyVariant.SmallRegular} type={TypographyType.Body}>
                {previewCount()}
              </PreviewCount>
            </PreviewHeader>
            {renderPreview()}
          </SectionCard>
        </PreviewPanel>
      </SettingsLayout>

      <FormActions>{formButtons}</FormActions>
    </>
  );
};
